/* eslint-disable no-restricted-globals, no-unused-vars */
// ─────────────────────────────────────────────────────────────────────────
// SERGA // PERSONAL OS — 3D world (three.js WebGL ambiance + projected cards)
// Cards are HTML positioned by projecting their 3D anchors each frame: they
// parallax as you orbit, scale with depth, and the focused one zooms to fill.
// Drill-down: overview (3 areas) → area (cards fan out) → card (zoom + expand).
// ─────────────────────────────────────────────────────────────────────────
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { buildModel, buildCard, cardInner, setInvestPeriod,
  setMood, setJournal, addCapture, keepCapture, discardCapture,
  toggleRoute, routeCapture, setCaptureFilter } from './cards.js';
import { clamp } from './dom.js';

const PALETTES = {
  teal:   { a: 0x2fe6d4, line: 0x0e8a8f },
  cyan:   { a: 0x49b6ff, line: 0x16557f },
  amber:  { a: 0xffb454, line: 0x8a5a1e },
  violet: { a: 0xb07cff, line: 0x4a3a8a },
};
const Vec = (x, y, z) => new THREE.Vector3(x, y, z);
const BASE_DIST = 42;               // depth at which a card renders at scale 1

const ZONE_POS = {
  'z-projects': Vec(2, 15, 2),
  'z-finances': Vec(21, 9, -4),
  'z-travel':   Vec(12, 2, -2),
  'z-cloud':    Vec(-18, 21, -11),   // remote home node — orbits further out
  'z-vitals':   Vec(-20, 5, 7),      // the operator — close in, off to the left
  'z-capture':  Vec(26, -3, 5),      // quick inbox — low on the right, easy reach
  'z-system':   Vec(0, -17, -9),     // infrastructure — sunk beneath the field
};
// built-in zones with fixed anchors above (vs runtime EXTRA_ZONES, auto-placed)
const FIXED_ZONES = ['z-projects', 'z-finances', 'z-travel', 'z-cloud', 'z-vitals', 'z-capture', 'z-system'];
const CORE = Vec(8, -4, 0);          // root of the constellation (you)

export function createWorld({ canvas, cardsLayer, settings, onLevelChange }) {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(innerWidth, innerHeight);

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0x03070c, 0.0040);

  const camera = new THREE.PerspectiveCamera(50, innerWidth / innerHeight, 0.1, 3000);
  camera.position.set(9, 8, 54);

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.07;
  controls.minDistance = 16;
  controls.maxDistance = 110;
  controls.rotateSpeed = 0.5;
  controls.enablePan = false;

  let pal = PALETTES[settings.accent] || PALETTES.teal;

  // ── drag-layout overrides ───────────────────────────────────────────────
  // The operator can pick up any card and drop it elsewhere in the field.
  // Zone moves are stored as absolute positions; item moves as an offset from
  // their zone centre (so they ride along if the zone itself is later moved).
  // Stored separately from the content draft so a layout reset is independent.
  const LAYOUT_KEY = 'serga-os-layout-v1';
  const LAYOUT = (() => {
    try { const r = JSON.parse(localStorage.getItem(LAYOUT_KEY) || '{}');
      return { zones: r.zones || {}, items: r.items || {} }; }
    catch (e) { return { zones: {}, items: {} }; }
  })();
  const persistLayout = () => { try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(LAYOUT)); } catch (e) {} };
  // a zone's live centre = its drag override if any, else its default anchor
  function zoneCenter(id) {
    const ov = LAYOUT.zones[id];
    if (ov) return new THREE.Vector3(ov.x, ov.y, ov.z);
    return (ZONE_POS[id] || CORE).clone();
  }
  // screen px → world point on the plane through ndcZ (the card's current depth)
  function unprojectAt(cx, cy, ndcZ) {
    return new THREE.Vector3(
      (cx / innerWidth) * 2 - 1, -(cy / innerHeight) * 2 + 1, ndcZ).unproject(camera);
  }
  // store a card's dropped position; zones absolute, items relative to zone centre
  function saveCardPos(rec) {
    if (rec.kind === 'zone') {
      LAYOUT.zones[rec.id] = { x: rec.anchor.x, y: rec.anchor.y, z: rec.anchor.z };
    } else {
      const c = zoneCenter(rec.zoneId);
      LAYOUT.items[rec.id] = { dx: rec.anchor.x - c.x, dy: rec.anchor.y - c.y, dz: rec.anchor.z - c.z };
    }
    persistLayout();
  }
  // redraw the settled connecting lines for the current level (used live mid-drag)
  function rebuildSettledLines() {
    if (state.level === 0) {
      setLines(model.zones.map(z => [CORE.clone(), cards.get(z.id).anchor.clone()]));
    } else if (state.level === 1) {
      const c = zoneCenter(state.zoneId);
      const items = model.zones.find(z => z.id === state.zoneId).items.map(it => cards.get(it.id));
      setLines(items.map(it => [c.clone(), it.anchor.clone()]));
    }
  }
  // can this card be picked up right now? (zones at overview, items inside a zone,
  // never while a transition is mid-flight or the field is locked)
  function canDragRec(rec) {
    if (locked || tween.active) return false;
    if (rec.fx || rec.collapse || rec.grow || rec.posTween) return false;
    if (rec.kind === 'zone') return state.level === 0;
    return state.level === 1 && rec.zoneId === state.zoneId;
  }
  // selectors that own the pointer themselves (don't hijack them for a drag)
  const DRAG_IGNORE = 'button, input, a, textarea, select, [data-period], [data-mood], [data-goto], [data-cap-keep], [data-cap-route], [data-cap-discard], [data-cap-to], [data-cap-filter], [data-journal], [data-capture]';

  // ── glow texture ──────────────────────────────────────────────────────────
  const glowTex = (() => {
    const c = document.createElement('canvas'); c.width = c.height = 128;
    const g = c.getContext('2d');
    const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    grd.addColorStop(0, 'rgba(255,255,255,0.9)');
    grd.addColorStop(0.3, 'rgba(255,255,255,0.22)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.fillRect(0, 0, 128, 128);
    return new THREE.CanvasTexture(c);
  })();

  // ── starfield ─────────────────────────────────────────────────────────────
  let stars;
  function buildStars(count) {
    if (stars) { scene.remove(stars); stars.geometry.dispose(); }
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 160 + Math.random() * 700;
      const th = Math.random() * Math.PI * 2, ph = Math.acos(2 * Math.random() - 1);
      pos[i*3] = r * Math.sin(ph) * Math.cos(th);
      pos[i*3+1] = r * Math.sin(ph) * Math.sin(th);
      pos[i*3+2] = r * Math.cos(ph);
    }
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    stars = new THREE.Points(g, new THREE.PointsMaterial({
      color: 0x9fd8de, size: 1.0, sizeAttenuation: true, transparent: true,
      opacity: 0.7, depthWrite: false, blending: THREE.AdditiveBlending }));
    scene.add(stars);
  }
  buildStars(settings.stars ? 2400 : 800);

  const grid = new THREE.PolarGridHelper(150, 16, 14, 96, pal.line, pal.line);
  grid.material.transparent = true; grid.material.opacity = 0.08; grid.position.y = -32;
  scene.add(grid);

  const coreGlow = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glowTex, color: pal.a, transparent: true, opacity: 0.7,
    blending: THREE.AdditiveBlending, depthWrite: false }));
  coreGlow.scale.set(6, 6, 1); coreGlow.position.copy(CORE); scene.add(coreGlow);

  // ── lighting + image-based env — only the saucer's PBR materials respond;
  //    the rest of the scene (sprites / points / lines) is unlit. The IBL gives
  //    the metal body something to reflect so it doesn't render near-black. ──
  scene.add(new THREE.AmbientLight(0x9fc2cc, 0.45));
  const keyLight = new THREE.DirectionalLight(0xdff4f5, 1.05);
  keyLight.position.set(24, 46, 34); scene.add(keyLight);
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.02).texture;

  // ── flying saucer — recurring background fly-bys ──────────────────────────
  const saucer = { root: null, spin: null, light: null, glow: null, loaded: false,
    active: null, nextAt: 6, _yaw: null, _bank: 0 };
  const SAUCER_GAP = 28;                      // seconds between fly-bys (± randomised)
  const rnd = (a, b) => a + Math.random() * (b - a);
  (function loadSaucer() {
    const draco = new DRACOLoader();
    draco.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    const loader = new GLTFLoader(); loader.setDRACOLoader(draco);
    loader.load((process.env.PUBLIC_URL || '') + '/assets/saucer.glb', gltf => {
      const m = gltf.scene;
      // normalise: recenter to origin + scale to a known size (Blender units vary)
      const box = new THREE.Box3().setFromObject(m);
      const ctr = box.getCenter(new THREE.Vector3());
      const sz = box.getSize(new THREE.Vector3());
      m.position.sub(ctr);
      const maxDim = Math.max(sz.x, sz.y, sz.z) || 1;
      m.traverse(o => { if (o.isMesh) { o.frustumCulled = false;
        if (o.material) { o.material.emissive = new THREE.Color(pal.a); o.material.emissiveIntensity = 0.16; } } });
      const spin = new THREE.Group(); spin.scale.setScalar(5.2 / maxDim); spin.add(m);
      const beam = m.getObjectByName('ScanBeam');
      if (beam) {
        // re-anchor the cone so it telescopes downward from the saucer's base
        beam.geometry.computeBoundingBox();
        const topLocal = beam.geometry.boundingBox.max.y;
        beam.position.y = beam.position.y + topLocal;   // place top at the base
        beam.geometry.translate(0, -topLocal, 0);        // top → local origin
        beam.scale.y = 0.0001; beam.visible = false;
        const bm = beam.material;
        bm.transparent = true; bm.depthWrite = false; bm.blending = THREE.AdditiveBlending;
        bm.side = THREE.DoubleSide; bm.opacity = 0;
        bm.color = new THREE.Color(pal.a); bm.emissive = new THREE.Color(pal.a); bm.emissiveIntensity = 0.85;
      }
      const root = new THREE.Group(); root.rotation.order = 'YXZ'; root.add(spin);
      const light = new THREE.PointLight(pal.a, 0, 80, 2); root.add(light);
      const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex, color: pal.a,
        transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
      glow.scale.set(16, 16, 1); root.add(glow);
      root.visible = false; scene.add(root);
      Object.assign(saucer, { root, spin, light, glow, beam, loaded: true });
    }, undefined, err => console.warn('saucer load failed', err));
  })();

  const bez = (a, c, b, t) => { const u = 1 - t; return new THREE.Vector3(
    u*u*a.x + 2*u*t*c.x + t*t*b.x,
    u*u*a.y + 2*u*t*c.y + t*t*b.y,
    u*u*a.z + 2*u*t*c.z + t*t*b.z); };
  const easeOut = x => 1 - Math.pow(1 - x, 3);
  const easeIn = x => x * x * x;
  // pop-from-a-point with a touch of overshoot
  const easeOutBack = x => { const c1 = 1.5, c3 = c1 + 1; return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2); };
  // drill choreography timings (seconds): card collapses → lines extend → cards bloom
  const COLLAPSE_DUR = 0.32, LINE_DUR = 0.42, GROW_DUR = 0.48, GROW_STAGGER = 0.06;
  const BEAM_EXT = 0.5, BEAM_HOLD = 2.0, BEAM_RET = 0.5;   // telescope timings (s)
  function setBeam(sy, op) {
    if (!saucer.beam) return;
    saucer.beam.visible = sy > 0.002;
    saucer.beam.scale.y = Math.max(0.0001, sy);
    saucer.beam.material.opacity = op;
  }
  function orientSaucer(p, pa, level) {
    saucer.root.position.copy(p);
    const vx = pa.x - p.x, vy = pa.y - p.y, vz = pa.z - p.z;
    const yaw = Math.atan2(vx, vz);
    const pitch = -Math.atan2(vy, Math.hypot(vx, vz)) * 0.5;
    const prevYaw = saucer._yaw == null ? yaw : saucer._yaw;
    let dY = yaw - prevYaw; if (dY > Math.PI) dY -= 2*Math.PI; if (dY < -Math.PI) dY += 2*Math.PI;
    const bankTarget = level ? 0 : clamp(dY * 24, -0.55, 0.55);
    saucer._bank += (bankTarget - saucer._bank) * 0.08;
    saucer._yaw = yaw;
    saucer.root.rotation.set(level ? pitch * 0.25 : pitch, yaw, saucer._bank);
  }
  function endFlyby(t) {
    saucer.root.visible = false; saucer.active = null; setBeam(0, 0);
    saucer.nextAt = t + SAUCER_GAP * rnd(0.6, 1.7);
  }
  // a random stop point, drawn from one of several open regions of the field
  // so it roams widely but still lands somewhere catchable (not dead-centre).
  function randomStop() {
    const r = Math.random();
    if (r < 0.30) return new THREE.Vector3(rnd(-40, 40), rnd(20, 34), rnd(-12, 18));   // high sky
    if (r < 0.48) return new THREE.Vector3(rnd(-54, -26), rnd(2, 26), rnd(-24, 18));   // left
    if (r < 0.66) return new THREE.Vector3(rnd(26, 54), rnd(2, 26), rnd(-24, 18));     // right
    if (r < 0.86) return new THREE.Vector3(rnd(-50, 50), rnd(8, 34), -rnd(34, 66));    // deep background
    return new THREE.Vector3(rnd(-30, 40), rnd(-13, 1), rnd(-12, 18));                 // below the cards
  }
  // an off-screen entry/exit point in a random direction around the stop
  function offscreenAround(S) {
    const ang = rnd(0, Math.PI * 2), d = rnd(64, 102);
    return new THREE.Vector3(S.x + Math.cos(ang) * d, S.y + rnd(4, 24), S.z + Math.sin(ang) * d * 0.5);
  }
  function startFlyby(forceScan) {
    const dir = Math.random() < 0.5 ? 1 : -1;
    // Scan-and-beam sequence (primary) vs an occasional silent distant drift.
    // NOTE: the WebGL layer composites *behind* the HTML cards, so stops are
    // biased toward the open regions of the field rather than dead-centre.
    const scan = forceScan != null ? forceScan : Math.random() < 0.7;
    if (scan) {
      const S = randomStop();
      const A = offscreenAround(S), B = offscreenAround(S);
      const Cin = new THREE.Vector3((A.x + S.x) / 2 + rnd(-10, 10), Math.max(A.y, S.y) + rnd(2, 9), (A.z + S.z) / 2 + rnd(-8, 8));
      const Cout = new THREE.Vector3((B.x + S.x) / 2 + rnd(-10, 10), Math.max(B.y, S.y) + rnd(2, 9), (B.z + S.z) / 2 + rnd(-8, 8));
      saucer.active = { kind: 'scan', A, Cin, S, Cout, B,
        tIn: rnd(2.2, 4.0), tHover: BEAM_EXT + BEAM_HOLD + BEAM_RET, tOut: rnd(2.2, 4.0),
        scl: rnd(0.95, 1.5), t0: clock.elapsedTime };
    } else {
      const y = rnd(26, 44), z = -rnd(150, 230);
      const A = new THREE.Vector3(dir * rnd(180, 240), y, z);
      const B = new THREE.Vector3(-dir * rnd(180, 240), y + rnd(-12, 12), z + rnd(-30, 30));
      const C = new THREE.Vector3(rnd(-40, 60), y + rnd(18, 46), z + rnd(-20, 20));
      saucer.active = { kind: 'drift', A, C, B, dur: rnd(15, 22), scl: rnd(2.4, 3.4), t0: clock.elapsedTime };
    }
    saucer._yaw = null; saucer._bank = 0;
    saucer.root.scale.setScalar(saucer.active.scl);
    setBeam(0, 0);
    saucer.root.visible = true;
  }


  // ── model + cards ─────────────────────────────────────────────────────────
  const model = buildModel();
  const cards = new Map();
  const _v = new THREE.Vector3();

  function makeCard(desc, zoneId) {
    const el = buildCard(desc);
    el.style.opacity = '0';
    cardsLayer.appendChild(el);
    const glow = new THREE.Sprite(new THREE.SpriteMaterial({
      map: glowTex, color: desc.kind === 'zone' ? pal.a : 0x0c2730,
      transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
    glow.scale.set(15, 11, 1); scene.add(glow);
    const rec = { ...desc, el, glow, zoneId, anchor: new THREE.Vector3(),
      dispPos: new THREE.Vector3(), posTween: null, focusT: 0, _wset: false, _op: 0 };
    el.addEventListener('click', e => { if (dragMoved) return; e.stopPropagation(); handleClick(rec); });
    // period selector on the investments card — swap window + re-render in place
    el.addEventListener('click', e => {
      const seg = e.target.closest('[data-period]');
      if (!seg) return;
      e.stopPropagation();
      setInvestPeriod(seg.dataset.period);
      reinner(rec);
    }, true);
    // linked-project chip on a travel card — jump to that project node
    el.addEventListener('click', e => {
      const go = e.target.closest('[data-goto]');
      if (!go) return;
      e.stopPropagation();
      gotoZone('z-projects'); setTimeout(() => gotoItem(go.dataset.goto), 520);
    }, true);
    // VITALS mind tap + CAPTURE triage — delegated, mutate then re-render in place
    el.addEventListener('click', e => {
      const t = e.target;
      const mood = t.closest('[data-mood]');
      const capKeep = t.closest('[data-cap-keep]');
      const capRoute = t.closest('[data-cap-route]');
      const capDiscard = t.closest('[data-cap-discard]');
      const capTo = t.closest('[data-cap-to]');
      const capFilter = t.closest('[data-cap-filter]');
      let hit = true;
      if (mood) setMood(mood.dataset.mood);
      else if (capTo) routeCapture(capTo.dataset.capTo, capTo.dataset.proj);
      else if (capKeep) keepCapture(capKeep.dataset.capKeep);
      else if (capRoute) toggleRoute(capRoute.dataset.capRoute);
      else if (capDiscard) discardCapture(capDiscard.dataset.capDiscard);
      else if (capFilter) setCaptureFilter(capFilter.dataset.capFilter);
      else hit = false;
      if (hit) {
        e.stopPropagation();
        reinner(rec);
        if (mood) { const j = rec.el.querySelector('[data-journal]'); if (j) j.focus(); }
      }
    }, true);
    // CAPTURE field + MIND journal — Enter commits
    el.addEventListener('keydown', e => {
      if (e.key !== 'Enter') return;
      const cap = e.target.closest && e.target.closest('[data-capture]');
      const jr = e.target.closest && e.target.closest('[data-journal]');
      if (cap) { e.preventDefault(); if (addCapture(cap.value)) { reinner(rec); const i = rec.el.querySelector('[data-capture]'); if (i) i.focus(); } }
      else if (jr) { e.preventDefault(); setJournal(jr.value); jr.blur(); reinner(rec); }
    });
    // commit the MIND journal on blur too (so a click away saves the line)
    el.addEventListener('blur', e => {
      const jr = e.target.closest && e.target.closest('[data-journal]');
      if (jr) setJournal(jr.value);
    }, true);
    const closeBtn = el.querySelector('.c-close');
    if (closeBtn) closeBtn.addEventListener('click', e => { e.stopPropagation(); back(); });

    // ── drag to reposition — pick the card up and drop it anywhere in the field.
    //    A small move-threshold keeps a plain click as "drill in"; once you cross
    //    it the move is committed and the click is suppressed. Pointer capture
    //    keeps tracking even if the cursor outruns the card.
    let drag = null;
    el.addEventListener('pointerdown', e => {
      if (e.button !== 0 || e.target.closest(DRAG_IGNORE) || !canDragRec(rec)) return;
      const z = rec.anchor.clone().project(camera).z;
      const grab = unprojectAt(e.clientX, e.clientY, z);
      drag = { id: e.pointerId, ndcZ: z, offset: rec.anchor.clone().sub(grab),
        x0: e.clientX, y0: e.clientY, moved: false };
      try { el.setPointerCapture(e.pointerId); } catch (_) {}
    });
    el.addEventListener('pointermove', e => {
      if (!drag || drag.id !== e.pointerId) return;
      if (!drag.moved) {
        if (Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) < 5) return;
        drag.moved = true; dragMoved = true; rec.dragging = true;
        rec.posTween = null;
        el.classList.add('dragging'); document.body.classList.add('card-dragging');
      }
      const w = unprojectAt(e.clientX, e.clientY, drag.ndcZ).add(drag.offset);
      rec.anchor.copy(w); rec.dispPos.copy(w);
      rebuildSettledLines();
      e.preventDefault();
    });
    const endDrag = e => {
      if (!drag || drag.id !== e.pointerId) return;
      try { el.releasePointerCapture(e.pointerId); } catch (_) {}
      const moved = drag.moved; drag = null;
      rec.dragging = false;
      el.classList.remove('dragging'); document.body.classList.remove('card-dragging');
      if (moved) { saveCardPos(rec); setTimeout(() => { dragMoved = false; }, 0); }
    };
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);

    cards.set(desc.id, rec);
    return rec;
  }
  model.zones.forEach(z => { makeCard(z, null); z.items.forEach(it => makeCard(it, z.id)); });

  // ── lines ──────────────────────────────────────────────────────────────────
  const lineGroup = new THREE.Group(); scene.add(lineGroup);
  const lineRecs = [];          // { line, a, b } — a=dot origin, b=anchor target
  let lineGrow = null;          // { at, dur } — schedules the extend animation
  function setLines(pairs, grow = false) {
    lineGroup.clear(); lineRecs.length = 0; lineGrow = null;
    pairs.forEach(([a, b]) => {
      const A = a.clone(), B = b.clone();
      const end = grow ? A.clone() : B.clone();   // start collapsed at the dot if growing
      const g = new THREE.BufferGeometry().setFromPoints([A, end]);
      const line = new THREE.Line(g, new THREE.LineBasicMaterial({
        color: pal.line, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending }));
      lineGroup.add(line);
      lineRecs.push({ line, a: A, b: B });
    });
  }
  function startLineGrow(at, dur, reverse = false) { lineGrow = { at, dur, reverse }; }

  // ── navigation state ───────────────────────────────────────────────────────
  const state = { level: 0, zoneId: null, itemId: null };
  let locked = false;                  // pre-auth gate: all cards suppressed
  let reverseTimer = null;             // pending overview-settle after a reverse drill
  const clock = new THREE.Clock();

  // spawn a staggered position tween: cards animate from `from` out to their anchor
  function spawnFanOut(recs, from, stagger = 0.07, dur = 0.75, base = 0.12) {
    recs.forEach((rec, i) => {
      rec.dispPos.copy(from);
      rec.posTween = { from: from.clone(), to: rec.anchor.clone(),
        start: clock.elapsedTime, delay: base + i * stagger, dur };
    });
  }

  function placeOverview() {
    let extra = 0;
    model.zones.forEach(z => {
      if (!ZONE_POS[z.id]) ZONE_POS[z.id] = computeExtraZonePos(extra++);
      else if (!FIXED_ZONES.includes(z.id)) extra++;
      cards.get(z.id).anchor.copy(zoneCenter(z.id));
    });
  }
  // place runtime-spawned zones on an arc sweeping out from the core
  function computeExtraZonePos(i) {
    const ang = -0.5 - i * 0.85, r = 25;
    return Vec(8 + Math.cos(ang) * r, 20 - i * 6.5, -3 + Math.sin(ang) * r * 0.5);
  }
  function placeZone(zoneId) {
    const c = zoneCenter(zoneId);
    const items = model.zones.find(z => z.id === zoneId).items;
    const n = items.length, cols = Math.min(3, n), rows = Math.ceil(n / cols);
    const sx = 16, sy = 13.5;
    items.forEach((it, i) => {
      const ov = LAYOUT.items[it.id];
      if (ov) { cards.get(it.id).anchor.set(c.x + ov.dx, c.y + ov.dy, c.z + ov.dz); return; }
      const col = i % cols, row = Math.floor(i / cols);
      const ox = (col - (cols - 1) / 2) * sx;
      const oy = -(row - (rows - 1) / 2) * sy;
      cards.get(it.id).anchor.set(c.x + ox, c.y + oy, c.z);
    });
  }

  function applyVisibility() {
    cards.forEach(rec => {
      if (locked) { rec._targetOp = 0; rec._targetGlow = 0; rec.el.classList.remove('expanded'); return; }
      let vis = false;
      if (state.level === 0) vis = rec.kind === 'zone';
      else if (state.level === 1) vis = rec.zoneId === state.zoneId;
      else if (state.level === 2) vis = rec.id === state.itemId;
      rec._targetOp = vis ? 1 : 0;
      rec._targetGlow = vis ? (rec.kind === 'zone' ? 0.5 : 0.3) : 0;
      // toggle expanded content; sizing + travel are animated in the render loop.
      // while a focus transition (fx) is running, it owns the .expanded class so the
      // big content only appears once the card has collapsed to a point.
      if (!rec.fx) rec.el.classList.toggle('expanded', state.level === 2 && rec.id === state.itemId);
    });
  }

  // ── camera tween ───────────────────────────────────────────────────────────
  const tween = { active: false, t: 0, dur: 1, fromP: new THREE.Vector3(), toP: new THREE.Vector3(),
    fromT: new THREE.Vector3(), toT: new THREE.Vector3() };
  function flyCam(toP, toT, dur = 1) {
    tween.fromP.copy(camera.position); tween.toP.copy(toP);
    tween.fromT.copy(controls.target); tween.toT.copy(toT);
    tween.t = 0; tween.dur = dur; tween.active = true; controls.enabled = false;
  }
  const ease = x => x < 0.5 ? 4*x*x*x : 1 - Math.pow(-2*x + 2, 3) / 2;

  // ── transitions ──────────────────────────────────────────────────────────────
  function gotoOverview() {
    clearTimeout(reverseTimer); reverseTimer = null;
    state.level = 0; state.zoneId = null; state.itemId = null;
    placeOverview();
    // clear any leftover drill animation state so zones fan back in cleanly
    cards.forEach(rec => { rec.collapse = null; rec.grow = null; rec.scaleMul = 1; rec._opForce = null; });
    applyVisibility();
    flyCam(Vec(9, 8, 54), Vec(9, 8, 0), 1.2);
    spawnFanOut(model.zones.map(z => cards.get(z.id)), CORE.clone(), 0.09, 0.85, 0.1);
    setLines(model.zones.map(z => [CORE.clone(), cards.get(z.id).anchor.clone()]));
    emit();
  }
  // Drill into a zone. fromItem=true means we're stepping back up from an open
  // card (the zone card is already gone) so we skip the collapse beat.
  function gotoZone(zoneId, fromItem = false) {
    clearTimeout(reverseTimer); reverseTimer = null;
    const c = zoneCenter(zoneId);
    const zoneCard = cards.get(zoneId);
    const items = model.zones.find(z => z.id === zoneId).items.map(it => cards.get(it.id));
    state.level = 1; state.zoneId = zoneId; state.itemId = null;
    placeZone(zoneId);

    // hide everything that isn't part of this drill
    cards.forEach(rec => {
      if (rec === zoneCard || items.includes(rec)) return;
      rec.collapse = null; rec.grow = null; rec._opForce = null; rec.scaleMul = 1;
      rec._targetOp = 0; rec._targetGlow = 0;
      rec.el.classList.remove('expanded');
    });

    const t0 = clock.elapsedTime;
    let lineAt, growAt;
    if (!fromItem) {
      // 1) the clicked card shrinks into a single dot at the centre
      zoneCard.grow = null;
      zoneCard.collapse = { t0, dur: COLLAPSE_DUR };
      zoneCard._targetGlow = 0;
      lineAt = t0 + COLLAPSE_DUR;
      growAt = lineAt + LINE_DUR;
    } else {
      // returning from a card — no dot to collapse, lines + cards come straight back
      zoneCard.collapse = null; zoneCard.grow = null;
      zoneCard.scaleMul = 0; zoneCard._opForce = 0; zoneCard._targetOp = 0; zoneCard._targetGlow = 0;
      lineAt = t0;
      growAt = t0 + LINE_DUR * 0.7;
    }

    // 2) lines extend out of the dot toward each sub-card slot
    setLines(items.map(it => [c.clone(), it.anchor.clone()]), true);
    startLineGrow(lineAt, LINE_DUR);

    // 3) once the lines arrive, each sub-card blooms up from a pixel
    items.forEach((it, i) => {
      it.collapse = null; it.posTween = null;
      it.dispPos.copy(it.anchor);
      it.scaleMul = 0; it._op = 0; it._opForce = 0; it._targetOp = 1; it._targetGlow = 0;
      it.el.classList.remove('expanded');
      it.grow = { startAt: growAt, dur: GROW_DUR, delay: i * GROW_STAGGER };
    });

    flyCam(Vec(c.x, c.y, c.z + 44), Vec(c.x, c.y, c.z), 1.1);
    emit();
  }
  function gotoItem(itemId) {
    clearTimeout(reverseTimer); reverseTimer = null;
    const rec = cards.get(itemId);
    state.level = 2; state.zoneId = rec.zoneId; state.itemId = itemId;
    // focus transition: the clicked card pinches into a point at centre, then the
    // full panel blooms up from it (same choreography as the zone → list drill).
    rec.fx = { t0: clock.elapsedTime, dir: 'open' };
    applyVisibility();
    const h = rec.anchor;
    flyCam(Vec(h.x, h.y, h.z + 32), Vec(h.x, h.y, h.z), 0.9);
    setLines([]);
    emit();
  }
  function handleClick(rec) {
    if (rec.kind === 'zone') { if (state.level === 0) gotoZone(rec.id); }
    else { if (state.level === 1) gotoItem(rec.id); }
  }
  // Reverse of the gotoZone drill, played when stepping a zone's card list back
  // up to the overview. Mirror choreography: each sub-card shrinks back into a
  // point at its slot (reverse stagger) → the lines retract into the dot → the
  // zone card blooms back out of that dot → the rest of the field fans in and
  // the camera pulls back. Reuses the existing collapse/grow render branches.
  function reverseZone(zoneId) {
    clearTimeout(reverseTimer); reverseTimer = null;
    const c = zoneCenter(zoneId);
    const zoneCard = cards.get(zoneId);
    const items = model.zones.find(z => z.id === zoneId).items.map(it => cards.get(it.id));
    const n = items.length;
    const t0 = clock.elapsedTime;
    const SUB_DUR = 0.34;          // per sub-card shrink-to-point
    const lineAt = t0 + SUB_DUR + Math.max(0, n - 1) * GROW_STAGGER;
    const zoneAt = lineAt + LINE_DUR;
    const phaseA = (zoneAt - t0) + GROW_DUR;

    // 1) each sub-card shrinks back into a point at its slot (reverse stagger of
    //    the bloom, so the last one to appear is the first to fold away)
    items.forEach((it, i) => {
      it.grow = null; it.posTween = null; it.fx = null;
      it.dispPos.copy(it.anchor);
      it.el.classList.remove('expanded');
      it.collapse = { t0: t0 + Math.max(0, n - 1 - i) * GROW_STAGGER, dur: SUB_DUR };
      it._targetGlow = 0;
    });

    // 2) the lines retract from each slot back into the dot
    setLines(items.map(it => [c.clone(), it.anchor.clone()]));   // start fully extended
    startLineGrow(lineAt, LINE_DUR, true);

    // 3) the zone card blooms back out of the dot, exactly where it collapsed from
    zoneCard.posTween = null; zoneCard.collapse = null;
    zoneCard.dispPos.copy(c);
    zoneCard.scaleMul = 0; zoneCard._op = 0; zoneCard._opForce = 0;
    zoneCard._targetOp = 1; zoneCard._targetGlow = 0.5;
    zoneCard.el.classList.remove('expanded');
    zoneCard.grow = { startAt: zoneAt, dur: GROW_DUR, delay: 0 };

    // as the zone card finishes reforming, settle back into the full overview
    reverseTimer = setTimeout(() => finishReverse(zoneId),
      Math.max(0, phaseA - GROW_DUR * 0.45) * 1000);
  }
  function finishReverse(zoneId) {
    reverseTimer = null;
    state.level = 0; state.zoneId = null; state.itemId = null;
    placeOverview();
    // the just-reformed zone card holds its place; the others fan in from the core
    model.zones.forEach(z => {
      const rec = cards.get(z.id);
      if (z.id === zoneId) { rec.posTween = null; }
      else { rec.collapse = null; rec.grow = null; rec.scaleMul = 1; rec._opForce = null; }
    });
    const others = model.zones.filter(z => z.id !== zoneId).map(z => cards.get(z.id));
    spawnFanOut(others, CORE.clone(), 0.08, 0.7, 0.04);
    setLines(model.zones.map(z => [CORE.clone(), cards.get(z.id).anchor.clone()]));
    applyVisibility();
    flyCam(Vec(9, 8, 54), Vec(9, 8, 0), 1.15);
    emit();
  }
  function back() {
    if (locked) return;
    if (state.level === 2) {
      // reverse the bloom: the panel shrinks back into a point, then the list returns
      const rec = cards.get(state.itemId);
      const zid = state.zoneId;
      if (rec) rec.fx = { t0: clock.elapsedTime, dir: 'close', then: () => gotoZone(zid, true) };
      else gotoZone(zid, true);
    }
    else if (state.level === 1) {
      // CLOUD is a portal node (no card list to fold) — return to the field plainly;
      // every other zone plays the reverse-drill choreography.
      if (state.zoneId === 'z-cloud') gotoOverview();
      else reverseZone(state.zoneId);
    }
  }

  // ── auth gate ───────────────────────────────────────────────────────────────
  // lock(): collapse the constellation to nothing — only the ambient field
  // (stars, core glow, saucer fly-bys) keeps running behind the login card.
  function lock() {
    clearTimeout(reverseTimer); reverseTimer = null;
    locked = true;
    state.level = 0; state.zoneId = null; state.itemId = null;
    cards.forEach(rec => {
      rec.collapse = null; rec.grow = null; rec.posTween = null;
      rec.scaleMul = 0; rec._opForce = 0; rec._op = 0;
      rec._targetOp = 0; rec._targetGlow = 0;
      rec.el.classList.remove('expanded');
      rec.el.style.opacity = '0'; rec.el.style.pointerEvents = 'none';
    });
    setLines([]);
    camera.position.set(9, 8, 54); controls.target.set(9, 8, 0); controls.update();
    emit();
  }
  // unlock(): the zones bloom out of the core just like a fresh boot.
  function unlock() {
    if (!locked) return;
    locked = false;
    gotoOverview();
  }
  function emit() {
    onLevelChange && onLevelChange({
      level: state.level,
      zone: state.zoneId ? model.zones.find(z => z.id === state.zoneId) : null,
      item: state.itemId ? cards.get(state.itemId) : null,
    });
  }

  placeOverview();
  setLines(model.zones.map(z => [CORE.clone(), cards.get(z.id).anchor.clone()]));
  applyVisibility();

  // ── live edit API (driven by the SERGA SHELL) ───────────────────────────────
  function getContext() { return { level: state.level, zoneId: state.zoneId, itemId: state.itemId }; }
  function listing() {
    return model.zones.map(z => ({
      id: z.id,
      label: z.data.label || z.data.title || z.id,
      items: z.items.map(it => ({ id: it.id, label: it.data.title || it.data.label || it.id })),
    }));
  }
  function rebindClose(rec) {
    const btn = rec.el.querySelector('.c-close');
    if (btn) btn.addEventListener('click', e => { e.stopPropagation(); back(); });
  }
  function reinner(rec) {
    const wasExpanded = rec.el.classList.contains('expanded');
    rec.el.innerHTML = cardInner(rec);
    if (wasExpanded) rec.el.classList.add('expanded');
    rebindClose(rec);
  }
  // field edit — re-render just the affected card(s) in place
  function refreshNode(id) {
    const ids = (id === 'fin-runway' || id === 'fin-invest' || id === 'fin-budget') ? ['fin-runway', 'fin-invest', 'fin-budget'] : [id];
    ids.forEach(cid => { const rec = cards.get(cid); if (rec) reinner(rec); });
  }
  // structural edit — reconcile cards against a fresh model, then re-settle view
  function sync() {
    const fresh = buildModel();
    const want = new Map();
    fresh.zones.forEach(z => {
      want.set(z.id, { desc: z, zone: null });
      z.items.forEach(it => want.set(it.id, { desc: it, zone: z.id }));
    });
    // drop cards that no longer exist
    [...cards.keys()].forEach(id => {
      if (!want.has(id)) {
        const r = cards.get(id);
        r.el.remove(); scene.remove(r.glow); r.glow.material.dispose(); cards.delete(id);
      }
    });
    // create cards that just appeared
    want.forEach((w, id) => { if (!cards.has(id)) makeCard(w.desc, w.zone); });
    model.zones = fresh.zones;
    cards.forEach(reinner);
    // repair the camera target if the current node was deleted
    if (state.level === 2 && !cards.has(state.itemId)) { state.level = 1; state.itemId = null; }
    if (state.level >= 1 && !model.zones.find(z => z.id === state.zoneId)) { state.level = 0; state.zoneId = null; state.itemId = null; }
    if (state.level === 0) gotoOverview();
    else if (state.level === 1) gotoZone(state.zoneId, true);
    else gotoItem(state.itemId);
  }

  // wipe every drag override and re-settle the current view at the defaults
  function resetLayout() {
    LAYOUT.zones = {}; LAYOUT.items = {}; persistLayout();
    if (state.level === 0) gotoOverview();
    else if (state.level === 1) gotoZone(state.zoneId, true);
    else { const zid = state.zoneId, iid = state.itemId; placeZone(zid); gotoItem(iid); }
  }

  // ── drag guard ───────────────────────────────────────────────────────────────
  let dragMoved = false, downXY = null;
  const onCanvasDown = e => { downXY = [e.clientX, e.clientY]; dragMoved = false; };
  const onPointerMove = e => {
    if (downXY && Math.hypot(e.clientX - downXY[0], e.clientY - downXY[1]) > 6) dragMoved = true;
  };
  const onPointerUp = () => setTimeout(() => { dragMoved = false; downXY = null; }, 0);
  canvas.addEventListener('pointerdown', onCanvasDown);
  addEventListener('pointermove', onPointerMove);
  addEventListener('pointerup', onPointerUp);

  // ── render loop ──────────────────────────────────────────────────────────────
  let idle = settings.motion !== false;
  let rafId = 0, running = true;
  function animate() {
    if (!running) return;
    rafId = requestAnimationFrame(animate);
    const dt = clock.getDelta(), t = clock.elapsedTime;

    if (tween.active) {
      tween.t += dt / tween.dur;
      const k = ease(Math.min(tween.t, 1));
      camera.position.lerpVectors(tween.fromP, tween.toP, k);
      controls.target.lerpVectors(tween.fromT, tween.toT, k);
      camera.lookAt(controls.target);
      if (tween.t >= 1) { tween.active = false; controls.enabled = state.level !== 2; }
    } else controls.update();

    const warp = tween.active ? 1 : 0;
    if (stars) stars.rotation.y += dt * (0.004 + warp * 0.03);
    coreGlow.material.opacity = 0.5 + Math.sin(t * 1.4) * 0.16;

    // ── saucer fly-by ────────────────────────────────────────────────────
    if (saucer.loaded) {
      const fb = saucer.active;
      if (fb) {
        saucer.spin.rotation.y += dt * 0.7;                  // slow continuous spin
        if (fb.kind === 'drift') {
          const lt = (t - fb.t0) / fb.dur;
          if (lt >= 1) endFlyby(t);
          else {
            orientSaucer(bez(fb.A, fb.C, fb.B, lt), bez(fb.A, fb.C, fb.B, Math.min(lt + 0.012, 1)), false);
            const k = clamp(Math.min(lt, 1 - lt) / 0.12, 0, 1);
            saucer.glow.material.opacity = 0.5 * k; saucer.glow.scale.setScalar(22);
            saucer.light.intensity = 1.5 * k; saucer.light.distance = 170;
            setBeam(0, 0);
          }
        } else {                                             // scan-and-beam sequence
          const e = t - fb.t0, total = fb.tIn + fb.tHover + fb.tOut;
          if (e >= total) endFlyby(t);
          else {
            // craft glow (fade in/out at the very ends of the whole sequence)
            const gk = clamp(Math.min(e, total - e) / 0.6, 0, 1);
            saucer.glow.material.opacity = 0.55 * gk; saucer.glow.scale.setScalar(15);
            saucer.light.intensity = 1.9 * gk; saucer.light.distance = 80;
            if (e < fb.tIn) {                                // ARRIVE (decelerate to stop)
              const k = easeOut(e / fb.tIn);
              orientSaucer(bez(fb.A, fb.Cin, fb.S, k), bez(fb.A, fb.Cin, fb.S, Math.min(k + 0.02, 1)), false);
              setBeam(0, 0);
            } else if (e < fb.tIn + fb.tHover) {             // HOVER + telescope beam
              const h = e - fb.tIn, bob = Math.sin(h * 1.6) * 0.22;
              saucer.root.position.set(fb.S.x, fb.S.y + bob, fb.S.z);
              saucer._bank += (0 - saucer._bank) * 0.1;      // level out so beam points straight down
              saucer.root.rotation.set(saucer.root.rotation.x * 0.9, saucer._yaw || 0, saucer._bank);
              let bs;
              if (h < BEAM_EXT) bs = easeOut(h / BEAM_EXT);                       // extend
              else if (h < BEAM_EXT + BEAM_HOLD) bs = 1;                          // hold (2s)
              else bs = 1 - easeIn((h - BEAM_EXT - BEAM_HOLD) / BEAM_RET);        // retract
              bs = Math.max(0, bs);
              setBeam(bs, 0.55 * bs);
            } else {                                         // DEPART (accelerate away)
              const k = easeIn((e - fb.tIn - fb.tHover) / fb.tOut);
              orientSaucer(bez(fb.S, fb.Cout, fb.B, k), bez(fb.S, fb.Cout, fb.B, Math.min(k + 0.02, 1)), false);
              setBeam(0, 0);
            }
          }
        }
      } else if (t >= saucer.nextAt && idle !== false) {
        startFlyby();
      }
    }

    // ── drill lines: telescope out from the collapsed dot to each anchor ──
    if (lineGrow && t >= lineGrow.at) {
      const raw = clamp((t - lineGrow.at) / lineGrow.dur, 0, 1);
      const k = lineGrow.reverse ? 1 - ease(raw) : ease(raw);   // reverse = retract into the dot
      for (const r of lineRecs) {
        const pos = r.line.geometry.attributes.position;
        pos.setXYZ(1, r.a.x + (r.b.x - r.a.x) * k, r.a.y + (r.b.y - r.a.y) * k, r.a.z + (r.b.z - r.a.z) * k);
        pos.needsUpdate = true;
      }
      if (raw >= 1) lineGrow = null;                            // guard on raw: in reverse k ends at 0
    }

    const W = innerWidth, H = innerHeight;
    const cx = W * 0.5, cy = H * 0.52;
    const fullW = Math.min(1060, Math.round(W * 0.84));
    cards.forEach(rec => {
      // ── fast path: fully hidden + not animating → skip projection + DOM ──
      // Most cards are off-screen at any level (only one zone's items show), so
      // this avoids projecting and re-writing styles for them every frame.
      if (rec._op < 0.012 && (rec._targetOp ?? 0) === 0 && rec._opForce == null
          && !rec.fx && !rec.collapse && !rec.grow && !rec.posTween && rec.focusT < 0.012) {
        if (rec._op !== 0) { rec._op = 0; rec.el.style.opacity = '0'; rec.el.style.pointerEvents = 'none'; rec._lastOpS = '0'; }
        if (rec.glow.material.opacity > 0.001)
          rec.glow.material.opacity += ((rec._targetGlow ?? 0) - rec.glow.material.opacity) * 0.1;
        return;
      }
      // ── drill: clicked card collapses into a single dot ──────────────────
      if (rec.collapse) {
        const k = easeIn(clamp((t - rec.collapse.t0) / rec.collapse.dur, 0, 1));
        rec.scaleMul = 1 - k;
        rec._opForce = 1 - k * 0.35;            // stays lit as it shrinks to a point
        if (k >= 1) { rec.scaleMul = 0; rec._opForce = 0; rec.collapse = null; }
      }
      // ── drill: sub-card blooms up from a pixel at the end of its line ─────
      if (rec.grow) {
        const e = t - rec.grow.startAt - (rec.grow.delay || 0);
        if (e <= 0) { rec.scaleMul = 0; rec._opForce = 0; }
        else {
          const p = clamp(e / rec.grow.dur, 0, 1);
          rec.scaleMul = easeOutBack(p);
          rec._opForce = clamp(p * 2.2, 0, 1);
          rec._targetGlow = rec.kind === 'zone' ? 0.5 : 0.3;
          if (p >= 1) { rec.scaleMul = 1; rec._opForce = null; rec.grow = null; }
        }
      }

      // position tween (fan-out from origin / settle to anchor)
      const pt = rec.posTween;
      if (pt) {
        const e = t - pt.start - pt.delay;
        if (e <= 0) rec.dispPos.copy(pt.from);
        else {
          rec.dispPos.lerpVectors(pt.from, pt.to, ease(Math.min(e / pt.dur, 1)));
          if (e >= pt.dur) rec.posTween = null;
        }
      } else rec.dispPos.copy(rec.anchor);

      // idle bob (settled overview zones only)
      let by = 0;
      if (idle && state.level === 0 && rec.kind === 'zone' && !rec.posTween && !rec.dragging)
        by = Math.sin(t * 0.6 + rec.anchor.x) * 0.5;
      _v.set(rec.dispPos.x, rec.dispPos.y + by, rec.dispPos.z);
      rec.glow.position.copy(_v);
      _v.project(camera);
      const behind = _v.z > 1;
      const sx = (_v.x * 0.5 + 0.5) * W;
      const sy = (-_v.y * 0.5 + 0.5) * H;
      const dist = camera.position.distanceTo(rec.glow.position);

      // ── focus transition: pinch-to-point then bloom (open), reverse (close) ──
      if (rec.fx) {
        const fx = rec.fx;
        const gridS0 = clamp(BASE_DIST / dist, 0.5, 1.35);
        if (fx.sx0 == null) {            // lock the card's launch point on frame 1
          fx.sx0 = sx; fx.sy0 = sy; fx.s0 = gridS0 * (rec.scaleMul ?? 1);
        }
        const e = t - fx.t0;
        const D1 = 0.24, D2 = GROW_DUR;   // collapse-to-point, then bloom
        let px, py, sc, big;
        if (fx.dir === 'open') {
          if (e < D1) {                   // 1) small card pinches to a point at centre
            const k = easeIn(clamp(e / D1, 0, 1));
            px = fx.sx0 + (cx - fx.sx0) * k;
            py = fx.sy0 + (cy - fx.sy0) * k;
            sc = fx.s0 * (1 - k); big = false;
          } else {                        // 2) full panel scales up out of the point
            const p = clamp((e - D1) / D2, 0, 1);
            px = cx; py = cy; sc = easeOutBack(p); big = true;
            if (p >= 1) { rec.fx = null; rec.focusT = 1; }
          }
        } else {                          // close — panel shrinks back into the point
          const k = easeIn(clamp(e / 0.3, 0, 1));
          px = cx; py = cy; sc = 1 - k; big = true;
          if (e >= 0.3) {
            rec.fx = null; rec.focusT = 0; big = false;
            rec.el.classList.remove('expanded');
            if (fx.then) fx.then();
          }
        }
        rec.el.classList.toggle('expanded', big);
        if (big) {
          rec.el.style.width = fullW + 'px';
          rec.el.style.height = 'auto';
          rec.el.style.maxHeight = Math.round(H * 0.82) + 'px';
          rec._wset = true;
        } else if (rec._wset) {
          rec.el.style.width = ''; rec.el.style.height = ''; rec.el.style.maxHeight = ''; rec._wset = false;
        }
        sc = Math.max(0, sc);
        if (rec._lastOpS !== '1') { rec.el.style.opacity = '1'; rec._lastOpS = '1'; }
        rec.el.style.pointerEvents = sc > 0.85 ? 'auto' : 'none';
        const tf = `translate(-50%,-50%) translate(${px.toFixed(1)}px,${py.toFixed(1)}px) scale(${sc.toFixed(3)})`;
        if (tf !== rec._lastTf) { rec.el.style.transform = tf; rec._lastTf = tf; }
        rec.el.style.zIndex = '3000';
        rec.glow.material.opacity += (0 - rec.glow.material.opacity) * 0.1;
        return;
      }

      // fade
      if (rec._opForce != null) rec._op = behind ? 0 : rec._opForce;
      else rec._op += ((behind ? 0 : (rec._targetOp ?? 0)) - rec._op) * 0.12;
      const opS = rec._op.toFixed(3);
      if (opS !== rec._lastOpS) { rec.el.style.opacity = opS; rec._lastOpS = opS; }
      rec.el.style.pointerEvents = rec._op > 0.6 ? 'auto' : 'none';
      rec.glow.material.opacity += ((rec._targetGlow ?? 0) - rec.glow.material.opacity) * 0.1;

      // focus blend: the opened card glides to centre and grows
      const expanded = state.level === 2 && rec.id === state.itemId;
      rec.focusT += ((expanded ? 1 : 0) - rec.focusT) * 0.16;
      const f = rec.focusT;
      const ef = f < 0.5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2;
      const gridS = clamp(BASE_DIST / dist, 0.5, 1.35);
      const tx = sx + (cx - sx) * ef;
      const ty = sy + (cy - sy) * ef;
      const s = (gridS + (1 - gridS) * ef) * (rec.scaleMul ?? 1);
      const tf = `translate(-50%,-50%) translate(${tx.toFixed(1)}px,${ty.toFixed(1)}px) scale(${s.toFixed(3)})`;
      if (tf !== rec._lastTf) { rec.el.style.transform = tf; rec._lastTf = tf; }
      rec.el.style.zIndex = String(f > 0.02 ? 3000 : Math.round(2000 - dist * 4));

      // grow the panel width in sync with the focus blend
      if (f > 0.004) {
        rec.el.style.width = Math.round(218 + (fullW - 218) * ef) + 'px';
        rec.el.style.height = 'auto';
        rec.el.style.maxHeight = Math.round(H * 0.82) + 'px';
        rec._wset = true;
      } else if (rec._wset) {
        rec.el.style.width = ''; rec.el.style.height = ''; rec.el.style.maxHeight = '';
        rec._wset = false;
      }
    });

    renderer.render(scene, camera);
  }
  animate();

  // ── live settings ───────────────────────────────────────────────────────────
  function applyAccent(name) {
    pal = PALETTES[name] || PALETTES.teal;
    coreGlow.material.color.setHex(pal.a);
    grid.material.color.setHex(pal.line);
    lineGroup.children.forEach(l => l.material.color.setHex(pal.line));
    cards.forEach(rec => { if (rec.kind === 'zone') rec.glow.material.color.setHex(pal.a); });
    if (saucer.loaded) {
      saucer.light.color.setHex(pal.a);
      saucer.glow.material.color.setHex(pal.a);
      saucer.spin.traverse(o => { if (o.isMesh && o.material && o.material.emissive) o.material.emissive.setHex(pal.a); });
      if (saucer.beam) saucer.beam.material.color.setHex(pal.a);
    }
  }
  function setSettings(s) {
    if (s.accent) applyAccent(s.accent);
    if (s.stars !== undefined) buildStars(s.stars ? 2400 : 800);
    if (s.motion !== undefined) idle = s.motion;
  }
  function resize() {
    camera.aspect = innerWidth / innerHeight; camera.updateProjectionMatrix();
    renderer.setSize(innerWidth, innerHeight);
    applyVisibility();
  }
  addEventListener('resize', resize);

  // ── teardown — stop the loop, drop listeners, free the GL context ──────────
  function dispose() {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    canvas.removeEventListener('pointerdown', onCanvasDown);
    removeEventListener('pointermove', onPointerMove);
    removeEventListener('pointerup', onPointerUp);
    removeEventListener('resize', resize);
    controls.dispose();
    cards.forEach(rec => { if (rec.el && rec.el.parentNode) rec.el.parentNode.removeChild(rec.el); });
    cards.clear();
    cardsLayer.innerHTML = '';
    // dispose() frees GPU resources but keeps the context valid. We deliberately
    // do NOT forceContextLoss(): under React StrictMode the effect re-mounts on
    // the SAME canvas, and a lost context would make the next renderer fail with
    // a null gl (getShaderPrecisionFormat on null). On a real unmount React drops
    // the canvas node and the context is GC'd.
    renderer.dispose();
  }

  return { gotoOverview, gotoZone, gotoItem, back, setSettings, state, dispose,
    lock, unlock, isLocked: () => locked,
    getContext, listing, refreshNode, sync, resetLayout,
    flyby: (close) => { if (saucer.loaded) { saucer.active = null; startFlyby(close); } },
    _obj: saucer,
    enterItemById: (zoneId, itemId) => { gotoZone(zoneId); setTimeout(() => gotoItem(itemId), 520); } };
}
